import React, { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, AlertTriangle, XCircle, RefreshCw, Database, BookOpen, Calendar, GitBranch, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function token() {
  return localStorage.getItem("academiaos_token");
}
function authHeaders() {
  return { Authorization: `Bearer ${token()}` };
}

// ─── Status badge ──────────────────────────────────────────────
function Badge({ label, variant = "default" }) {
  const styles = {
    MIGRATED:            "bg-emerald-100 text-emerald-800 border-emerald-200",
    SYSTEM_BOOTSTRAPPED: "bg-amber-100 text-amber-800 border-amber-200",
    QUARANTINED:         "bg-red-100 text-red-800 border-red-200",
    CONFIGURED:          "bg-sky-100 text-sky-800 border-sky-200",
    SUCCESS:             "bg-emerald-100 text-emerald-800 border-emerald-200",
    FAILED:              "bg-red-100 text-red-800 border-red-200",
    SKIPPED:             "bg-slate-100 text-slate-500 border-slate-200",
    default:             "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles[label] || styles.default}`}>
      {label}
    </span>
  );
}

// ─── Section heading ──────────────────────────────────────────────
function SectionHead({ title, subtitle, icon: Icon }) {
  return (
    <div className="flex items-start gap-3 mb-6">
      <div className="p-2 bg-slate-800 rounded-lg">
        <Icon className="w-5 h-5 text-amber-400" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {subtitle && <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

// ─── Authority Status Panel ────────────────────────────────────────
function AuthorityStatus({ status, onRefresh }) {
  if (!status) return null;
  const { canonical_pg_counts: c, blockers, data_gaps_remaining } = status;
  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">ISB Canonical Authority Status</h3>
        <button onClick={onRefresh} className="text-slate-400 hover:text-white transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[
          { label: "Programmes", val: c.program },
          { label: "Courses", val: c.course },
          { label: "Academic Years", val: c.academic_year },
          { label: "Org Units", val: c.organizational_unit },
          { label: "Terms", val: c.academic_term },
          { label: "Course Offerings", val: c.course_offering },
          { label: "Sections", val: c.section },
          { label: "Credit Schemes", val: c.credit_scheme },
        ].map(({ label, val }) => (
          <div key={label} className="bg-slate-900 rounded-lg p-3 text-center">
            <div className={`text-2xl font-bold ${val > 0 ? "text-emerald-400" : "text-slate-600"}`}>{val}</div>
            <div className="text-xs text-slate-400 mt-1">{label}</div>
          </div>
        ))}
      </div>
      {data_gaps_remaining?.length > 0 && (
        <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-400 mb-2">Data Gaps (requires admin configuration):</p>
          <ul className="space-y-1">
            {data_gaps_remaining.map(g => (
              <li key={g} className="text-xs text-amber-300 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {g}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data_gaps_remaining?.length === 0 && (
        <div className="flex items-center gap-2 text-emerald-400 text-sm">
          <CheckCircle className="w-4 h-4" /> All canonical academic tables activated
        </div>
      )}
    </div>
  );
}

// ─── Tab: Migration ────────────────────────────────────────────────
function MigrationTab() {
  const [runs, setRuns] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadRuns = async () => {
    try {
      const { data } = await axios.get(`${API}/academic-admin/migration/runs`, { headers: authHeaders() });
      setRuns(data);
    } catch {}
  };

  useEffect(() => { loadRuns(); }, []);

  const triggerDryRun = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/academic-admin/migration/dry-run`, {}, { headers: authHeaders() });
      toast.success(`Dry-run complete. run_id: ${data.run_id.slice(0, 8)}…`);
      setActiveReport(data);
      loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally { setLoading(false); }
  };

  const commitRun = async (dryRunId) => {
    if (!window.confirm("Execute REAL migration? This will write to canonical PostgreSQL tables.")) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/academic-admin/migration/commit/${dryRunId}`, {}, { headers: authHeaders() });
      toast.success(`Migration committed. run_id: ${data.run_id.slice(0, 8)}…`);
      setActiveReport(data);
      loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally { setLoading(false); }
  };

  const viewReport = async (runId) => {
    try {
      const { data } = await axios.get(`${API}/academic-admin/migration/report/${runId}`, { headers: authHeaders() });
      setActiveReport(data);
    } catch {}
  };

  return (
    <div>
      <SectionHead title="ISB Canonical Migration" subtitle="DRY_RUN → review reconciliation gate → REAL" icon={Database} />

      <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-4 mb-6 text-sm text-amber-300">
        <strong>Reconciliation Gate:</strong> Always run DRY_RUN first. Review quarantine items before committing REAL. Quarantined records are never auto-promoted to canonical tables.
      </div>

      <div className="flex gap-3 mb-6">
        <button
          onClick={triggerDryRun}
          disabled={loading}
          data-testid="dry-run-btn"
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Run DRY_RUN
        </button>
      </div>

      {activeReport && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-xs text-slate-400">Run ID: <code className="text-slate-300">{activeReport.run_id?.slice(0,8)}…</code></span>
              <Badge label={activeReport.run_type} />
            </div>
            {activeReport.run_type === "DRY_RUN" && (
              <button
                onClick={() => commitRun(activeReport.run_id)}
                disabled={loading}
                data-testid="commit-migration-btn"
                className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" /> Commit REAL Run
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {[
              { label: "Migrated", val: activeReport.summary?.migrated, color: "text-emerald-400" },
              { label: "Bootstrapped", val: activeReport.summary?.bootstrapped, color: "text-amber-400" },
              { label: "Skipped", val: activeReport.summary?.skipped, color: "text-slate-400" },
              { label: "Quarantined", val: activeReport.summary?.quarantined, color: "text-red-400" },
              { label: "Failed", val: activeReport.summary?.failed, color: "text-red-500" },
            ].map(({ label, val, color }) => (
              <div key={label} className="bg-slate-900 rounded-lg p-3 text-center">
                <div className={`text-2xl font-bold ${color}`}>{val ?? 0}</div>
                <div className="text-xs text-slate-400 mt-1">{label}</div>
              </div>
            ))}
          </div>

          {/* Data Gaps */}
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Data Gaps (requires admin configuration)</p>
            <div className="space-y-1">
              {(activeReport.data_gaps || []).map(g => (
                <div key={g.concept} className="flex items-start gap-2 text-xs text-slate-300 bg-slate-900 rounded p-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span><strong>{g.concept}:</strong> {g.reason || g.action}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By Table */}
          {activeReport.by_table && Object.keys(activeReport.by_table).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Per Table</p>
              {Object.entries(activeReport.by_table).map(([table, entries]) => (
                <details key={table} className="mb-2">
                  <summary className="cursor-pointer text-sm text-slate-300 bg-slate-900 rounded px-3 py-2 flex items-center justify-between">
                    <span className="font-mono font-medium">{table}</span>
                    <span className="text-xs text-slate-500">{entries.length} entries</span>
                  </summary>
                  <div className="mt-1 space-y-1 pl-3">
                    {entries.map((e, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-400 bg-slate-900/50 rounded px-2 py-1">
                        <Badge label={e.classification} />
                        <span className="font-mono">{e.source_id?.slice(0,30)}</span>
                        {e.reason && <span className="text-slate-500 truncate max-w-xs">{e.reason?.slice(0,80)}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      )}

      {runs.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Migration Run History</p>
          <div className="space-y-2">
            {runs.map(r => (
              <div key={r.migration_run_id} className="flex items-center gap-3 bg-slate-800 rounded-lg border border-slate-700 px-4 py-3 text-sm">
                <Badge label={r.run_type} />
                <code className="text-slate-400 text-xs">{r.migration_run_id?.slice(0,8)}…</code>
                <span className="text-slate-300">M:{r.migrated} B:{r.bootstrapped} Q:{r.quarantined}</span>
                <button onClick={() => viewReport(r.migration_run_id)} className="ml-auto text-xs text-sky-400 hover:text-sky-300">View Report</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Calendar & Terms ─────────────────────────────────────────
function CalendarTab({ onRefreshStatus }) {
  const [calendars, setCalendars] = useState([]);
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [form, setForm] = useState({ view: "terms" }); // terms | new_term | new_cal
  const [newCal, setNewCal] = useState({ code: "SEM-2025-26", name: "Semester Calendar 2025-26", calendar_type: "SEMESTER" });
  const [newTerm, setNewTerm] = useState({ academic_year_id: "", term_type: "SEMESTER", sequence_no: 1, code: "ODD-2025", start_date: "", end_date: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [c, y, t] = await Promise.all([
        axios.get(`${API}/academic-admin/setup/calendars`, { headers: authHeaders() }),
        axios.get(`${API}/academic-admin/setup/years`, { headers: authHeaders() }),
        axios.get(`${API}/academic-admin/setup/terms`, { headers: authHeaders() }),
      ]);
      setCalendars(c.data); setYears(y.data); setTerms(t.data);
      if (y.data.length > 0 && !newTerm.academic_year_id) {
        setNewTerm(p => ({ ...p, academic_year_id: y.data[0].id }));
      }
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const createCalendar = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/academic-admin/setup/calendars`, newCal, { headers: authHeaders() });
      toast.success("Calendar created"); load(); setForm({ view: "terms" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const createTerm = async () => {
    if (!newTerm.academic_year_id || !newTerm.start_date || !newTerm.end_date) {
      toast.error("Academic year, start date and end date are required"); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/academic-admin/setup/terms`, newTerm, { headers: authHeaders() });
      toast.success("Term created — A3 attendance term selector unblocked"); load(); onRefreshStatus(); setForm({ view: "terms" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <SectionHead title="Academic Calendar & Terms" subtitle="CONFIGURE — registrar creates institutional term boundaries" icon={Calendar} />

      <div className="bg-slate-700/30 border border-slate-600/50 rounded-lg p-3 mb-5 text-xs text-slate-400">
        <strong className="text-amber-400">DATA GAP:</strong> No academic term boundaries exist in legacy Mongo data. Term must be explicitly configured by an authorized registrar. These become CONFIGURED canonical records — not migrated data.
      </div>

      {/* Academic Year summary */}
      {years.length > 0 && (
        <div className="mb-5">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Migrated Academic Years</p>
          <div className="space-y-2">
            {years.map(y => (
              <div key={y.id} className="flex items-center gap-3 bg-slate-800 rounded-lg border border-slate-700 px-4 py-3 text-sm">
                <Database className="w-4 h-4 text-emerald-400" />
                <span className="font-medium text-white">{y.code}</span>
                <span className="text-slate-400 text-xs">{y.start_date} → {y.end_date}</span>
                <Badge label="MIGRATED" />
                <span className="text-xs text-slate-500 ml-auto">{y.calendar_name} ({y.calendar_type})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Terms list */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Academic Terms</p>
          <button
            onClick={() => setForm({ view: form.view === "new_term" ? "terms" : "new_term" })}
            data-testid="add-term-btn"
            className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1"
          >
            + Configure Term
          </button>
        </div>

        {terms.length === 0 && (
          <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-4 text-sm text-red-300">
            No terms configured. A3 attendance term selector is currently blocked. Create a term to unblock it.
          </div>
        )}

        {terms.map(t => (
          <div key={t.id} className="flex items-center gap-3 bg-slate-800 rounded-lg border border-slate-700 px-4 py-3 text-sm mb-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span className="font-medium text-white">{t.code}</span>
            <span className="text-slate-400 text-xs">{t.start_date} → {t.end_date}</span>
            <Badge label="CONFIGURED" />
            <span className="text-xs text-slate-500 ml-auto">{t.year_code} · Seq {t.sequence_no}</span>
          </div>
        ))}
      </div>

      {/* New Term Form */}
      {form.view === "new_term" && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
          <h4 className="text-sm font-semibold text-white mb-4">Configure New Academic Term</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Academic Year *</label>
              <select value={newTerm.academic_year_id} onChange={e => setNewTerm(p => ({ ...p, academic_year_id: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm">
                <option value="">Select year…</option>
                {years.map(y => <option key={y.id} value={y.id}>{y.code}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Term Code *</label>
              <input value={newTerm.code} onChange={e => setNewTerm(p => ({ ...p, code: e.target.value }))}
                placeholder="ODD-2025" className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Term Type *</label>
              <select value={newTerm.term_type} onChange={e => setNewTerm(p => ({ ...p, term_type: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm">
                {["SEMESTER","TRIMESTER","QUARTER","SESSION","ANNUAL"].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Sequence #</label>
              <input type="number" value={newTerm.sequence_no} onChange={e => setNewTerm(p => ({ ...p, sequence_no: parseInt(e.target.value) || 1 }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Start Date *</label>
              <input type="date" value={newTerm.start_date} onChange={e => setNewTerm(p => ({ ...p, start_date: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">End Date *</label>
              <input type="date" value={newTerm.end_date} onChange={e => setNewTerm(p => ({ ...p, end_date: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={createTerm} disabled={loading} data-testid="save-term-btn"
              className="px-4 py-2 bg-sky-700 hover:bg-sky-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {loading ? "Saving…" : "Create Term (CONFIGURED)"}
            </button>
            <button onClick={() => setForm({ view: "terms" })} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Calendar creation (only if none exists) */}
      {calendars.length === 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
          <h4 className="text-sm font-semibold text-white mb-3">No Academic Calendar found. Create one first.</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <input value={newCal.code} onChange={e => setNewCal(p => ({ ...p, code: e.target.value }))}
              placeholder="Code (e.g. SEM-2025-26)" className="bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            <input value={newCal.name} onChange={e => setNewCal(p => ({ ...p, name: e.target.value }))}
              placeholder="Name" className="bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            <select value={newCal.calendar_type} onChange={e => setNewCal(p => ({ ...p, calendar_type: e.target.value }))}
              className="bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm">
              {["SEMESTER","TRIMESTER","QUARTER","SESSION","ANNUAL"].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <button onClick={createCalendar} disabled={loading} data-testid="create-calendar-btn"
            className="px-4 py-2 bg-sky-700 hover:bg-sky-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? "Creating…" : "Create Calendar (CONFIGURED)"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Course Offerings & Sections ──────────────────────────────
function OfferingsTab({ onRefreshStatus }) {
  const [courses, setCourses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [sections, setSections] = useState([]);
  const [newOff, setNewOff] = useState({ course_version_id: "", academic_term_id: "", delivery_mode: "ONSITE", capacity: "" });
  const [newSec, setNewSec] = useState({ course_offering_id: "", section_code: "SEC-001", room: "", schedule_block: "" });
  const [showOffForm, setShowOffForm] = useState(false);
  const [showSecForm, setShowSecForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [c, t, o, s] = await Promise.all([
        axios.get(`${API}/academic-admin/setup/courses`, { headers: authHeaders() }),
        axios.get(`${API}/academic-admin/setup/terms`, { headers: authHeaders() }),
        axios.get(`${API}/academic-admin/setup/course-offerings`, { headers: authHeaders() }),
        axios.get(`${API}/academic-admin/setup/sections`, { headers: authHeaders() }),
      ]);
      setCourses(c.data); setTerms(t.data); setOfferings(o.data); setSections(s.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const createOffering = async () => {
    if (!newOff.course_version_id || !newOff.academic_term_id) {
      toast.error("Select course and term"); return;
    }
    setLoading(true);
    try {
      const payload = { ...newOff, capacity: newOff.capacity ? parseInt(newOff.capacity) : null };
      await axios.post(`${API}/academic-admin/setup/course-offerings`, payload, { headers: authHeaders() });
      toast.success("Course offering created"); load(); onRefreshStatus(); setShowOffForm(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const createSection = async () => {
    if (!newSec.course_offering_id || !newSec.section_code) {
      toast.error("Select offering and enter section code"); return;
    }
    setLoading(true);
    try {
      const payload = { ...newSec, capacity: newSec.capacity ? parseInt(newSec.capacity) : null };
      await axios.post(`${API}/academic-admin/setup/sections`, payload, { headers: authHeaders() });
      toast.success("Section created — A3 attendance sessions can now be created"); load(); onRefreshStatus(); setShowSecForm(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <SectionHead title="Course Offerings & Sections" subtitle="CONFIGURE — create delivery instances and teaching subdivisions" icon={BookOpen} />

      {terms.length === 0 && (
        <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-4 mb-5 text-sm text-red-300">
          No academic terms configured yet. Create a term first in the Calendar & Terms tab before creating course offerings.
        </div>
      )}

      {/* Courses (migrated) */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Migrated Courses ({courses.length})</p>
          <button onClick={() => setShowOffForm(!showOffForm)} disabled={terms.length === 0} data-testid="add-offering-btn"
            className="text-xs text-sky-400 hover:text-sky-300 disabled:opacity-40 flex items-center gap-1">
            + Create Course Offering
          </button>
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {courses.map(c => (
            <div key={c.id} className="flex items-center gap-3 bg-slate-800 rounded px-3 py-2 text-xs">
              <Badge label={c.version_status === "DRAFT" ? "SYSTEM_BOOTSTRAPPED" : "MIGRATED"} />
              <span className="font-mono font-medium text-white">{c.course_code}</span>
              <span className="text-slate-400 truncate">{c.title}</span>
              <span className="text-slate-500 ml-auto">{c.credits} credits</span>
            </div>
          ))}
        </div>
      </div>

      {/* New Course Offering Form */}
      {showOffForm && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
          <h4 className="text-sm font-semibold text-white mb-4">Create Course Offering</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Course *</label>
              <select value={newOff.course_version_id} onChange={e => setNewOff(p => ({ ...p, course_version_id: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm">
                <option value="">Select course…</option>
                {courses.map(c => <option key={c.course_version_id} value={c.course_version_id}>{c.course_code}: {c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Term *</label>
              <select value={newOff.academic_term_id} onChange={e => setNewOff(p => ({ ...p, academic_term_id: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm">
                <option value="">Select term…</option>
                {terms.map(t => <option key={t.id} value={t.id}>{t.code} ({t.year_code})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Delivery Mode</label>
              <select value={newOff.delivery_mode} onChange={e => setNewOff(p => ({ ...p, delivery_mode: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm">
                {["ONSITE","ONLINE","HYBRID"].map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Capacity (optional)</label>
              <input type="number" value={newOff.capacity} onChange={e => setNewOff(p => ({ ...p, capacity: e.target.value }))}
                placeholder="60" className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={createOffering} disabled={loading} data-testid="save-offering-btn"
              className="px-4 py-2 bg-sky-700 hover:bg-sky-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {loading ? "Creating…" : "Create Offering (CONFIGURED)"}
            </button>
            <button onClick={() => setShowOffForm(false)} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Offerings list */}
      {offerings.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Course Offerings ({offerings.length})</p>
            <button onClick={() => setShowSecForm(!showSecForm)} data-testid="add-section-btn"
              className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
              + Create Section
            </button>
          </div>
          <div className="space-y-1">
            {offerings.map(o => (
              <div key={o.id} className="flex items-center gap-3 bg-slate-800 rounded px-3 py-2 text-xs">
                <Badge label="CONFIGURED" />
                <span className="font-mono font-medium text-white">{o.course_code}</span>
                <span className="text-slate-400">{o.term_code}</span>
                <span className="text-slate-500">{o.delivery_mode}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Section Form */}
      {showSecForm && offerings.length > 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
          <h4 className="text-sm font-semibold text-white mb-3">Create Section</h4>
          <div className="bg-amber-900/20 border border-amber-700/40 rounded p-2 mb-4 text-xs text-amber-300">
            Section source: DATA_GAP — timetable_slots does not prove a Section exists. You are creating canonical institutional truth.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Course Offering *</label>
              <select value={newSec.course_offering_id} onChange={e => setNewSec(p => ({ ...p, course_offering_id: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm">
                <option value="">Select offering…</option>
                {offerings.map(o => <option key={o.id} value={o.id}>{o.course_code} — {o.term_code}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Section Code *</label>
              <input value={newSec.section_code} onChange={e => setNewSec(p => ({ ...p, section_code: e.target.value }))}
                placeholder="SEC-001" className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Room</label>
              <input value={newSec.room} onChange={e => setNewSec(p => ({ ...p, room: e.target.value }))}
                placeholder="Room 201" className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Schedule Block</label>
              <input value={newSec.schedule_block} onChange={e => setNewSec(p => ({ ...p, schedule_block: e.target.value }))}
                placeholder="MWF 09:00-10:30" className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={createSection} disabled={loading} data-testid="save-section-btn"
              className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {loading ? "Creating…" : "Create Section (CONFIGURED)"}
            </button>
            <button onClick={() => setShowSecForm(false)} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Sections list with Instructor Assignment */}
      {sections.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Sections ({sections.length}) — Instructor Assignment</p>
          <div className="space-y-2">
            {sections.map(s => (
              <SectionRowWithInstructor key={s.id} section={s} onUpdated={load} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section row with inline Instructor Assignment ─────────────────
function SectionRowWithInstructor({ section: s, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [partyInput, setPartyInput] = useState("");
  const [assignStatus, setAssignStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const assignInstructor = async () => {
    if (!partyInput.trim()) { toast.error("Enter a canonical party_id (UUID)"); return; }
    setSaving(true);
    setAssignStatus(null);
    try {
      const r = await axios.patch(
        `${API}/academic-admin/delivery/sections/${s.id}/instructor`,
        { party_id: partyInput.trim() },
        { headers: authHeaders() }
      );
      setAssignStatus(r.data);
      if (r.data.status === "ASSIGNED") { toast.success("Instructor assigned"); onUpdated(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Assignment failed"); }
    finally { setSaving(false); }
  };

  const instructorBadge = s.instructor_party_id
    ? <span className="text-amber-400 text-xs">IDENTITY_BRIDGE_REQUIRED</span>
    : <span className="text-slate-500 text-xs italic">Not assigned</span>;

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
      <div className="flex items-center gap-3 px-3 py-2.5 text-xs">
        <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
        <span className="font-mono font-medium text-white">{s.course_code} / {s.section_code}</span>
        <span className="text-slate-400">{s.term_code}</span>
        {s.room && <span className="text-slate-500 font-mono">{s.room}</span>}
        {s.schedule_block && <span className="text-slate-500 italic truncate max-w-32">{s.schedule_block}</span>}
        <Badge label="CONFIGURED" />
        <div className="ml-auto flex items-center gap-2">
          {instructorBadge}
          <button onClick={() => setExpanded(e => !e)} data-testid={`assign-instructor-btn-${s.id}`}
            className="text-sky-400 hover:text-sky-300 text-xs px-2 py-1 rounded border border-sky-700/40 hover:border-sky-500/60">
            {expanded ? "Close" : "Assign Instructor"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-700 px-4 py-3 bg-slate-900/60">
          <div className="bg-amber-900/20 border border-amber-700/40 rounded p-2 mb-3 text-xs text-amber-300">
            <strong>IDENTITY_BRIDGE_REQUIRED</strong> — All ISB faculty are currently in MongoDB only.
            Canonical party identity bridge has not been executed for this institution.
            Valid canonical party_id (ORGANIZATION or PERSON) required.
          </div>
          <label className="block text-xs text-slate-400 mb-1">Canonical party_id (UUID) *</label>
          <div className="flex gap-2">
            <input value={partyInput} onChange={e => setPartyInput(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              data-testid={`party-id-input-${s.id}`}
              className="flex-1 bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 text-xs font-mono" />
            <button onClick={assignInstructor} disabled={saving} data-testid={`save-instructor-btn-${s.id}`}
              className="px-3 py-2 bg-sky-700 hover:bg-sky-600 text-white rounded-lg text-xs disabled:opacity-50">
              {saving ? "…" : "Assign"}
            </button>
          </div>
          {assignStatus && (
            <div className={`mt-2 rounded p-2 text-xs ${
              assignStatus.status === "ASSIGNED"
                ? "bg-emerald-900/30 text-emerald-300 border border-emerald-700/40"
                : "bg-amber-900/30 text-amber-300 border border-amber-700/40"
            }`}>
              <strong>{assignStatus.status}</strong>: {assignStatus.message}
              {assignStatus.blocked_by && (
                <p className="mt-1 text-slate-400">{assignStatus.blocked_by}</p>
              )}
            </div>
          )}
          <p className="text-slate-600 text-xs mt-2 italic">
            Schedule: {s.schedule_block || "Not set"} · Room: {s.room || "Not set"}
            {" "}· <em>schedule_block is free text (MeetingPattern not modeled)</em>
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Timetable (Aggregate Read) ──────────────────────────────
function TimetableTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [termFilter, setTermFilter] = useState("");
  const [terms, setTerms] = useState([]);

  const load = async (term_id) => {
    setLoading(true);
    try {
      const params = term_id ? `?academic_term_id=${term_id}` : "";
      const r = await axios.get(`${API}/academic-admin/delivery/timetable${params}`, { headers: authHeaders() });
      setData(r.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/academic-admin/setup/terms`, { headers: authHeaders() })
      .then(r => setTerms(r.data)).catch(() => {});
    load("");
  }, []);

  const InstructorStatus = ({ status, partyId }) => {
    if (status === "ASSIGNED") return <span className="text-emerald-400 text-xs font-medium">Assigned</span>;
    if (status === "IDENTITY_BRIDGE_REQUIRED") return (
      <span className="text-amber-400 text-xs" title="Faculty identity bridge required">
        Identity bridge required
      </span>
    );
    return <span className="text-slate-500 text-xs italic">Not assigned</span>;
  };

  const groups = {};
  if (data?.timetable) {
    for (const entry of data.timetable) {
      const key = entry.term_code || "—";
      if (!groups[key]) groups[key] = [];
      groups[key].push(entry);
    }
  }

  return (
    <div>
      <SectionHead title="Canonical Timetable" subtitle="Aggregate read of Term → Offering → Section. No timetable table — this is the canonical approach." icon={Calendar} />

      {/* Schema Gaps Banner */}
      {data?.open_schema_gaps?.length > 0 && (
        <div className="mb-4 space-y-1">
          {data.open_schema_gaps.map((g, i) => (
            <div key={i} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-xs">
              <span className="text-amber-400 font-mono font-semibold">{g.gap}</span>
              <span className="text-slate-400 ml-2">{g.description}</span>
            </div>
          ))}
        </div>
      )}

      {/* Approved Dependencies */}
      {data?.approved_dependencies?.length > 0 && (
        <div className="mb-4 bg-amber-900/15 border border-amber-700/30 rounded-lg px-4 py-2.5">
          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1">Approved Dependencies</p>
          {data.approved_dependencies.map((d, i) => (
            <p key={i} className="text-xs text-amber-300/80">{d}</p>
          ))}
        </div>
      )}

      {/* Term filter */}
      <div className="flex items-center gap-3 mb-5">
        <select value={termFilter}
          onChange={e => { setTermFilter(e.target.value); load(e.target.value); }}
          className="bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm"
          data-testid="timetable-term-filter">
          <option value="">All Terms</option>
          {terms.map(t => <option key={t.id} value={t.id}>{t.code} ({t.year_code})</option>)}
        </select>
        <button onClick={() => load(termFilter)}
          className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
        {data && <span className="text-xs text-slate-500 ml-auto">{data.entry_count} entries</span>}
      </div>

      {loading && (
        <div className="text-center py-10 text-slate-400 text-sm">Loading timetable…</div>
      )}

      {!loading && data?.entry_count === 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center">
          <p className="text-slate-400 text-sm">No timetable data yet.</p>
          <p className="text-slate-500 text-xs mt-1">Create course offerings and sections in the Offerings & Sections tab.</p>
        </div>
      )}

      {!loading && Object.keys(groups).length > 0 && (
        <div className="space-y-5">
          {Object.entries(groups).map(([termCode, entries]) => (
            <div key={termCode}>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5" />
                Term: {termCode}
                <span className="font-normal normal-case">
                  {entries[0]?.term_start && `${entries[0].term_start} — ${entries[0].term_end}`}
                </span>
                <Badge label={entries[0]?.term_status || "—"} />
              </p>
              <div className="space-y-1.5">
                {entries.map((e, i) => (
                  <div key={i} className="bg-slate-800 rounded-xl border border-slate-700 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-semibold text-white text-sm">{e.course_code}</span>
                          <span className="text-slate-400 text-xs">{e.course_title}</span>
                          {e.credits && <span className="text-slate-500 text-xs">{e.credits} cr</span>}
                          <Badge label={e.delivery_mode} />
                        </div>
                        {e.section_code && (
                          <div className="flex items-center gap-3 text-xs text-slate-400">
                            <span className="font-mono text-slate-300">{e.section_code}</span>
                            {e.room && <span className="font-mono">{e.room}</span>}
                            {e.schedule_block && (
                              <span className="italic text-slate-500" title="Free text — MeetingPattern not modeled">
                                {e.schedule_block}
                              </span>
                            )}
                            {e.delivering_dept && <span className="text-slate-500">{e.delivering_dept}</span>}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-slate-500 mb-0.5">Instructor</p>
                        <InstructorStatus status={e.instructor_status} partyId={e.instructor_party_id} />
                        <p className="text-xs text-slate-600 mt-1.5">Students</p>
                        <span className="text-xs text-amber-600 italic" title={e.student_timetable_note}>
                          Blocked (identity bridge)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function ProgrammesTab() {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    axios.get(`${API}/academic-admin/setup/programs`, { headers: authHeaders() })
      .then(r => setPrograms(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <SectionHead title="Canonical Programmes" subtitle="Migrated from MongoDB — SYSTEM_BOOTSTRAPPED versions require admin confirmation" icon={GitBranch} />
      {programs.length === 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center text-slate-400">
          No programmes migrated yet. Run the ISB migration in the Migration tab.
        </div>
      )}
      <div className="space-y-3">
        {programs.map(p => (
          <div key={p.id} className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono font-semibold text-white">{p.code}</span>
                  <Badge label="MIGRATED" />
                  <Badge label={p.version_status?.toUpperCase()} />
                </div>
                <p className="text-sm text-slate-300">{p.title}</p>
                <p className="text-xs text-slate-500 mt-1">{p.degree_type} · {p.owning_dept || "No dept"}</p>
              </div>
              <div className="text-right text-xs text-slate-500">
                <p>Curriculum: {p.curriculum_status}</p>
                <p>Credits req: {p.total_credits_required ?? "—"}</p>
                <p>Credit scheme: {p.credit_scheme_id ? "Linked" : <span className="text-amber-400">Not set</span>}</p>
                <p>Grading scheme: {p.grading_scheme_id ? "Linked" : <span className="text-amber-400">Not set</span>}</p>
              </div>
            </div>
            {(p.version_status === "DRAFT" || p.curriculum_status === "DRAFT") && (
              <div className="mt-3 bg-amber-900/20 border border-amber-700/40 rounded p-2 text-xs text-amber-300">
                SYSTEM_BOOTSTRAPPED: Programme version is DRAFT. Credit scheme and grading scheme must be configured before this version can be published.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────
const TABS = [
  { id: "migration",  label: "Migration",          icon: Database },
  { id: "calendar",   label: "Calendar & Terms",    icon: Calendar },
  { id: "offerings",  label: "Offerings & Sections",icon: BookOpen },
  { id: "timetable",  label: "Timetable",           icon: BookOpen },
  { id: "programmes", label: "Programmes",          icon: GitBranch },
];

export default function AcademicOpsAdmin() {
  const [activeTab, setActiveTab] = useState("migration");
  const [status, setStatus] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/academic-admin/authority-status`, { headers: authHeaders() });
      setStatus(data);
    } catch {}
  };

  useEffect(() => { loadStatus(); }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-slate-400 text-xs mb-3">
            <span>Academic Operations</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">ISB Canonical Activation</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Academic Setup — Phase 1</h1>
          <p className="text-slate-400 text-sm mt-1">ISB Canonical Activation & Delivery Spine · Migrate what is known. Configure what is missing.</p>
        </div>

        {/* Authority Status */}
        <AuthorityStatus status={status} onRefresh={loadStatus} />

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-900 rounded-xl p-1 mb-6">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              data-testid={`tab-${id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium flex-1 justify-center transition-all ${
                activeTab === id
                  ? "bg-slate-700 text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="min-h-96">
          {activeTab === "migration"  && <MigrationTab />}
          {activeTab === "calendar"   && <CalendarTab onRefreshStatus={loadStatus} />}
          {activeTab === "offerings"  && <OfferingsTab onRefreshStatus={loadStatus} />}
          {activeTab === "timetable"  && <TimetableTab />}
          {activeTab === "programmes" && <ProgrammesTab />}
        </div>
      </div>
    </div>
  );
}
